/**
 * Sessions and password.
 *
 * Sessions are server-side and revocable — that is the whole reason the API
 * issues an opaque token rather than a JWT. A JWT cannot be un-issued, so
 * "sign out everywhere" would be a lie until it expired. This screen is where
 * that property becomes visible to the person it protects.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { api, ApiError, fmtSince, type SessionRow } from '../lib/api.js';
import { useSession } from '../lib/session.js';

export function SecurityScreen(): JSX.Element {
  const { status } = useSession();
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const demo = status === 'demo';

  const load = useCallback(async () => {
    if (demo) { setSessions([]); return; }
    try {
      const { sessions: rows } = await api.sessions();
      setSessions(rows);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load sessions.');
      setSessions([]);
    }
  }, [demo]);

  useEffect(() => { void load(); }, [load]);

  async function revoke(id: string): Promise<void> {
    setError(null);
    setBusy(id);
    try {
      await api.revokeSession(id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not end that session.');
    } finally {
      setBusy(null);
    }
  }

  async function revokeAll(): Promise<void> {
    setError(null);
    setBusy('all');
    try {
      // This signs out this device too. The session provider's 401 handler
      // takes it from here on the next request.
      await api.logoutAll();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not sign out everywhere.');
    } finally {
      setBusy(null);
    }
  }

  const live = (sessions ?? []).filter((s) => !s.revokedAt);

  return (
    <div className="scroll-area">
      <h2>Security</h2>

      {demo && (
        <div className="notice-bar" role="status">
          This deployment is in mock mode and you are using the seeded demo identity,
          which has no sessions and no password. Create a real account to use these.
        </div>
      )}

      {error && <div className="form-error" role="alert">{error}</div>}

      <section className="settings-block">
        <div className="section-head">
          <h3 className="panel-h">SIGNED IN · {live.length}</h3>
          {live.length > 1 && (
            <button
              className="btn btn-danger compact"
              onClick={() => void revokeAll()}
              disabled={busy !== null}
            >
              {busy === 'all' ? 'Signing out…' : 'Sign out everywhere'}
            </button>
          )}
        </div>

        {sessions === null && <p className="dim">Loading…</p>}
        {sessions !== null && live.length === 0 && (
          <p className="panel-empty">No active sessions.</p>
        )}

        <ul className="device-list">
          {live.map((s) => (
            <li key={s.id} className="device-row">
              <div className="device-main">
                <span className="device-label">
                  {s.userAgent ? shortUserAgent(s.userAgent) : 'Unknown client'}
                  {s.current && <span className="chip chip-ok device-here">This session</span>}
                </span>
                <span className="device-meta mono">
                  {s.ipAddress ?? 'no address'} · started {fmtSince(s.createdAt)} ·
                  {' '}last used {fmtSince(s.lastUsedAt)}
                </span>
              </div>
              {!s.current && (
                <button
                  className="btn btn-danger compact"
                  onClick={() => void revoke(s.id)}
                  disabled={busy !== null}
                >
                  {busy === s.id ? 'Ending…' : 'End'}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {!demo && <PasswordBlock />}
    </div>
  );
}

function PasswordBlock(): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setMessage(null);

    // Checked here as well as by the server: mistyping the confirmation is not
    // worth a round trip, and the server has no idea a confirmation field exists.
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }

    setPending(true);
    try {
      const res = await api.changePassword(current, next);
      setMessage(res.message);
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the password.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="settings-block">
      <h3 className="panel-h">PASSWORD</h3>
      <form onSubmit={onSubmit} className="settings-form" noValidate>
        <label className="field">
          <span className="field-label">Current password</span>
          <input
            className="input" type="password" required autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">New password</span>
          <input
            className="input" type="password" required autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)}
          />
          <span className="field-hint">At least 12 characters.</span>
        </label>
        <label className="field">
          <span className="field-label">Confirm new password</span>
          <input
            className="input" type="password" required autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
          />
        </label>

        {error && <div className="form-error" role="alert">{error}</div>}
        {message && <div className="form-ok" role="status">{message}</div>}

        <button className="btn btn-primary" type="submit" disabled={pending}>
          {pending ? 'Changing…' : 'Change password'}
        </button>
        <p className="field-hint">
          Changing your password signs out every other session, here and on your
          other devices.
        </p>
      </form>
    </section>
  );
}

/** Turn a user-agent string into something a person can recognise. */
function shortUserAgent(ua: string): string {
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : /AEGIS/i.test(ua) ? 'AEGIS desktop'
    : 'Unknown browser';
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Linux/.test(ua) ? 'Linux'
    : '';
  return os ? `${browser} on ${os}` : browser;
}
